import Head from "next/head";
import { GetServerSideProps } from "next";
import { ProductsMasonry } from "~/components/ProductsMasonry";
import { ProductsSlider } from "~/components/ProductsSlider";
import { SectionHeading } from "~/components/SectionHeading";
import { handleRequestError } from "~/utils/rollbar";
import { StrapiProduct, StrapiStore, StrapiSubCategory } from "~/shared-types";
import {
  defaultMediaFields,
  findRandomProducts,
  findRandomProductsBySubCategory,
  findStoresByMainCategories,
} from "~/strapi/api";
import { getRoute } from "~/utils/getRoute";
import { StoreInfo } from "./StoreInfo";
import Link from "next/link";
import { StoresSlider } from "~/components/StoresSlider";
import { ProductInfo } from "~/components/ProductInfo";
import { useSingleStoreAtom } from "~/atoms/singleStore";
import { getProduct } from "~/api/products";
import { getProductAttributes } from "~/api/productAttributes";

type ServerSideProps = {
  product: StrapiProduct;
  subCategoryProducts: StrapiProduct[];
  randomProducts: StrapiProduct[];
  similarStores: StrapiStore[];
  productAttributes: { id: number; name: string; }[];
};

export const getServerSideProps: GetServerSideProps<
  ServerSideProps,
  { productId: string }
> = async (context) => {
  const { productId } = context.params ?? {};

  if (!productId) return { notFound: true };

  const { data: product } = await getProduct(productId, {}).catch(
    handleRequestError(context.req, { data: null })
  );
  if (!product || !product.attributes.store.data) return { notFound: true };

  const storeId = product.attributes.store.data.id;
  const randomProducts = await findRandomProducts(storeId)?.catch(
    handleRequestError(context.req, [])
  );

  const subCategoryProductsQueryParams = {
    subCategoryId: product.attributes.subCategories.data[0].id,
    limit: 12,
  };
  const subCategoryProducts = await findRandomProductsBySubCategory(
    subCategoryProductsQueryParams
  ).catch(handleRequestError(context.req, []));

  const mainCategoryIds = product.attributes.subCategories.data
    .flatMap((subCategory) => subCategory.attributes.mainCategory?.data?.id)
    .filter(Boolean) as number[];

  const similarStores = await findStoresByMainCategories(mainCategoryIds, {
    populate: {
      storeImage: { fields: defaultMediaFields },
    },
    fields: ["name", "companyAddress"],
  })?.catch(handleRequestError(context.req, []));

  const productAttributesData = await getProductAttributes({}).catch(() => null);
  const productAttributes = (productAttributesData?.data ?? []).map((a: any) => ({ id: a.id, name: a.attributes.name }));
  return {
    props: { product, subCategoryProducts, randomProducts, similarStores, productAttributes },
  };
};

const ProductPage = ({
  product,
  subCategoryProducts,
  randomProducts,
  similarStores,
  productAttributes,
}: ServerSideProps) => {
  const { attributes } = product;
  const store = attributes.store.data;
  const { isLimited } = useSingleStoreAtom();

  const siteUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || "https://butikdanmark.dk";
  const productUrl = `${siteUrl}/produkter/${product.id}`;
  const imageUrl =
    attributes.images.data[0]?.attributes.url?.startsWith("http")
      ? attributes.images.data[0]?.attributes.url
      : `${process.env.NEXT_PUBLIC_STRAPI_BASE_URL || "https://strapi.butikdanmark.dk"}${
          attributes.images.data[0]?.attributes.url || ""
        }`;
  const productDescription =
    attributes.description ||
    `Køb ${attributes.name} fra ${store.attributes.name} på ButikDanmark.dk.`;
  const offerPrice = attributes.offerPrice ?? attributes.price;
  const availability =
    attributes.stockStatus === "inStock"
      ? "https://schema.org/InStock"
      : attributes.stockStatus === "unique"
      ? "https://schema.org/LimitedAvailability"
      : "https://schema.org/OutOfStock";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: attributes.name,
    description: productDescription,
    sku: String(product.id),
    image: imageUrl ? [imageUrl] : [],
    url: productUrl,
    brand: {
      "@type": "Organization",
      name: store.attributes.name,
    },
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "DKK",
      price: offerPrice?.toString() ?? "0",
      availability,
      seller: {
        "@type": "Organization",
        name: store.attributes.name,
      },
    },
  };

  return (
    <>
      <Head>
        <title>{`${attributes.name} – ${store.attributes.name} | Butik Danmark`}</title>
        <meta name="description" content={productDescription} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:title" content={`${attributes.name} – ${store.attributes.name}`} />
        <meta property="og:description" content={productDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={productUrl} />
        {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
        <meta property="og:site_name" content="Butik Danmark" />
        <meta property="og:locale" content="da_DK" />
        <meta name="twitter:card" content="summary_large_image" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      <ProductInfo product={product} productAttributes={productAttributes} />
      <StoreInfo className="mb-28" store={store} />
      <RandomProductSlider store={store} products={randomProducts} />
      {!isLimited && (
        <>
          <CategoryProducts
            category={attributes.subCategories.data[0]}
            products={subCategoryProducts}
          />
          <OtherStores similarStores={similarStores} currentStore={store} />
        </>
      )}
    </>
  );
};

type OtherStoresProps = {
  similarStores: StrapiStore[] | null;
  currentStore: StrapiStore | null;
};

export function OtherStores({ similarStores, currentStore }: OtherStoresProps) {
  if (!similarStores || similarStores.length === 0 || !currentStore)
    return null;
  const stores = similarStores.filter(
    (store) => store.id !== currentStore.id
  ) as StrapiStore[];
  return (
    <>
      <SectionHeading>
        <SectionHeading.Title>
          Andre butikker du vil synes om
        </SectionHeading.Title>
        <SectionHeading.Subtitle className="mb-20">
          Disse butikker har nogle af de samme varer som{" "}
          {currentStore.attributes.name}
        </SectionHeading.Subtitle>
      </SectionHeading>
      <StoresSlider stores={stores} className="mb-20" />
    </>
  );
}

type CategoryProductsProps = {
  category: StrapiSubCategory;
  products: StrapiProduct[];
};

export function CategoryProducts({
  category,
  products,
}: CategoryProductsProps) {
  const mainCategory = category?.attributes.mainCategory?.data;
  if (!mainCategory || products?.length === 0) return null;
  return (
    <>
      <SectionHeading>
        <SectionHeading.Title className="mb-5">
          Mere fra kategorien{" "}
          <Link
            href={getRoute(
              "/categories/:categoryId/:categoryName/:subCategoryId/:subCategoryName",
              {
                categoryId: mainCategory.id,
                categoryName: mainCategory.attributes.name,
                subCategoryId: category.id,
                subCategoryName: category.attributes.name,
              }
            )}
            className="text-primary"
          >
            {category.attributes.name}
          </Link>
        </SectionHeading.Title>
        <SectionHeading.Subtitle className="mb-20">
          Gå på udflugt i kategorien og find flere perler.
        </SectionHeading.Subtitle>
      </SectionHeading>
      <ProductsMasonry products={products} className="mb-28" />
    </>
  );
}

type RandomProductSliderProps = {
  store: StrapiStore | null;
  products: StrapiProduct[];
};

export function RandomProductSlider({
  store,
  products,
}: RandomProductSliderProps) {
  if (!store || products?.length === 0) return null;
  return (
    <div className="mb-28">
      <SectionHeading>
        <SectionHeading.Title className="mb-5">
          Mere fra{" "}
          <Link
            href={getRoute("/producenter/:slug", {
              slug: store.attributes?.slug || store.id,
            })}
            className="text-primary"
          >
            {store.attributes.name}
          </Link>
        </SectionHeading.Title>
        <SectionHeading.Subtitle className="mb-16">
          Her er et udpluk af spændende produkter fra samme sælger.
        </SectionHeading.Subtitle>
      </SectionHeading>
      <ProductsSlider products={products} className="mb-20" />
    </div>
  );
}

export default ProductPage;
